import { Lookup, useLookup } from '@/providers'
import { FeatureFlags, useFeatureFlag } from '@/providers/FeatureFlagContext'
import { RHFCheckbox, RHFRadioGroup, RHFTextField } from '@components/RHFInputs'
import { ActionCenterTask, MarketBasket } from '@domain'
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Chip, Divider, MenuItem, Skeleton, Stack, Typography } from '@mui/material'

import XlsTabs from './XlsTabs'

type Props = {
	marketBasket: MarketBasket
	loading: boolean
	onClose: () => void
	tasks: ActionCenterTask[]
	tasksLoading?: boolean
}

export default function ExcelExportForm({
	marketBasket,
	loading,
	onClose,
	tasks,
	tasksLoading = false,
}: Props) {
	const purchaseGroups = useLookup(Lookup.PurchaseGroups)
	const actionCenterEnabled = useFeatureFlag(FeatureFlags.ActionCenter)

	const completedCount = tasks?.filter((t) => t.completed).length ?? 0
	const totalCount = tasks?.length ?? 0
	const allComplete = totalCount > 0 && completedCount === totalCount

	return (
		<Stack spacing={2} sx={{ width: '48.125rem', p: 2 }}>
			<Stack spacing={2} direction="row" sx={{ width: '100%', mb: 2 }}>
				<Box sx={{ width: '100%' }}>
					<Typography variant="h6">{`Export to Excel (${marketBasket?.salesforceCaseNumber || ''})`}</Typography>
				</Box>
				<Stack
					spacing={2}
					direction="row"
					sx={{ width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
				>
					{actionCenterEnabled &&
						(tasksLoading ? (
							<Skeleton variant="rounded" width={120} height={24} />
						) : (
							<Chip
								size="small"
								color={allComplete ? 'success' : 'error'}
								icon={<FontAwesomeIcon icon={allComplete ? faCheck : faXmark} />}
								label={`Tasks ${completedCount}/${totalCount}`}
							/>
						))}

					<Button variant="text" onClick={onClose} color="secondary">
						Cancel
					</Button>
					<Button type="submit" form="basket-settings" variant="contained">
						Export
					</Button>
				</Stack>
			</Stack>

			<Typography variant="body1">
				{loading ? <Skeleton /> : marketBasket?.displayName || ''}
			</Typography>

			<Divider sx={{ pb: 1 }} />

			{/* Purchase Groups */}
			<RHFTextField name="groupBy" select size="small" label="Group Products by" fullWidth>
				{purchaseGroups.map((group) => (
					<MenuItem key={group.id} value={group.id}>
						{group.label}
					</MenuItem>
				))}
			</RHFTextField>

			<XlsTabs />

			{/* <Include purchases /> */}
			<RHFRadioGroup
				row
				fieldLabelPlacement="row"
				name="purchasesToInclude"
				label="Include purchases that are"
				size="small"
				options={[
					{ value: 'IN_BASKET', label: 'In the Basket' },
					{ value: 'ALL', label: 'All purchases' },
				]}
			/>

			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<RHFCheckbox name="highlightDirectDeal" />
				<Typography variant="body2">Highlight possible direct deals</Typography>
			</Stack>

			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<RHFCheckbox name="identifyOvercharges" />
				<Typography variant="body2">Identify items with Overcharges</Typography>
			</Stack>

			<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
				<RHFCheckbox name="markComplete" />
				<Typography variant="body2">Complete Market Basket on Export</Typography>
			</Stack>
		</Stack>
	)
}
